import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

// 플러그인 추가
dayjs.extend(relativeTime);

/**
 * 오늘 날짜를 YYYY-MM-DD 형식으로 반환
 * @returns {string} 예: "2024-03-14"
 */
export const getToday = () => {
  return dayjs().format("YYYY-MM-DD");
};

/**
 * 날짜를 YYYY-MM-DD 형식으로 변환
 * @param {Date | string | null} date - 변환할 날짜
 * @returns {string} 예: "2024-03-14"
 * @example
 * formatDate(new Date()) // "2024-03-14"
 * formatDate("2024-03-14") // "2024-03-14"
 * formatDate(null) // ""
 */
export const formatDate = (date: Date | string | null) => {
  if (!date) return "";
  return dayjs(date).format("YYYY-MM-DD");
};

/**
 * 날짜를 yyyymmdd 형태로 포맷팅
 * @param {Date | string | null} date - 포맷팅할 날짜
 * @returns {string} yyyymmdd 형태의 문자열
 * @example
 * formatDateToYYYYMMDD(new Date("2024-03-14")) // "20240314"
 * formatDateToYYYYMMDD("2024-03-14") // "20240314"
 * formatDateToYYYYMMDD(null) // ""
 */
export const formatDateToYYYYMMDD = (date: Date | string | null) => {
  if (!date) return "";
  return dayjs(date).format("YYYYMMDD");
};

/**
 * 두 날짜 사이의 일수 차이를 계산
 * @param {Date | string} startDate - 시작 날짜
 * @param {Date | string} endDate - 종료 날짜
 * @returns {number} 일수 차이
 * @example
 * getDaysDiff("2024-03-01", "2024-03-10") // 9
 * getDaysDiff(new Date("2024-03-01"), new Date("2024-03-10")) // 9
 */
export const getDaysDiff = (
  startDate: Date | string,
  endDate: Date | string
) => {
  return dayjs(endDate).diff(dayjs(startDate), "day");
};

/**
 * 특정 날짜가 오늘보다 이전인지 확인
 * @param {Date | string} date - 확인할 날짜
 * @returns {boolean} 이전 날짜 여부
 * @example
 * isPastDate("2024-03-01") // true
 * isPastDate("2024-03-20") // false
 */
export const isPastDate = (date: Date | string) => {
  return dayjs(date).isBefore(dayjs(), "day");
};

/**
 * 특정 날짜가 오늘보다 이후인지 확인
 * @param {Date | string} date - 확인할 날짜
 * @returns {boolean} 이후 날짜 여부
 * @example
 * isFutureDate("2024-03-20") // true
 * isFutureDate("2024-03-01") // false
 */
export const isFutureDate = (date: Date | string) => {
  return dayjs(date).isAfter(dayjs(), "day");
};

/**
 * 특정 날짜가 오늘인지 확인
 * @param {Date | string} date - 확인할 날짜
 * @returns {boolean} 오늘 날짜 여부
 * @example
 * isToday(new Date()) // true
 * isToday("2024-03-14") // true (오늘이 2024-03-14인 경우)
 */
export const isToday = (date: Date | string) => {
  return dayjs(date).isSame(dayjs(), "day");
};

/**
 * 날짜에 일수를 더하거나 뺌
 * @param {Date | string} date - 기준 날짜
 * @param {number} days - 더하거나 뺄 일수 (음수면 빼기)
 * @returns {Date} 계산된 날짜
 * @example
 * addDays("2024-03-14", 7) // 2024-03-21
 * addDays("2024-03-14", -7) // 2024-03-07
 */
export const addDays = (date: Date | string, days: number) => {
  return dayjs(date).add(days, "day").toDate();
};

/**
 * 날짜에 월을 더하거나 뺌
 * @param {Date | string} date - 기준 날짜
 * @param {number} months - 더하거나 뺄 월수 (음수면 빼기)
 * @returns {Date} 계산된 날짜
 * @example
 * addMonths("2024-03-14", 1) // 2024-04-14
 * addMonths("2024-03-14", -1) // 2024-02-14
 */
export const addMonths = (date: Date | string, months: number) => {
  return dayjs(date).add(months, "month").toDate();
};

/**
 * 날짜에 연도를 더하거나 뺌
 * @param {Date | string} date - 기준 날짜
 * @param {number} years - 더하거나 뺄 연도수 (음수면 빼기)
 * @returns {Date} 계산된 날짜
 * @example
 * addYears("2024-03-14", 1) // 2025-03-14
 * addYears("2024-03-14", -1) // 2023-03-14
 */
export const addYears = (date: Date | string, years: number) => {
  return dayjs(date).add(years, "year").toDate();
};

/**
 * 해당 월의 마지막 날짜를 반환
 * @param {Date | string} date - 기준 날짜
 * @returns {string} 마지막 날짜 (YYYY-MM-DD)
 * @example
 * getLastDayOfMonth("2024-03-14") // "2024-03-31"
 * getLastDayOfMonth("2024-02-14") // "2024-02-29" (윤년)
 */
export const getLastDayOfMonth = (date: Date | string) => {
  return formatDate(dayjs(date).endOf("month").toDate());
};

/**
 * 해당 월의 첫 날짜를 반환
 * @param {Date | string} date - 기준 날짜
 * @returns {string} 첫 날짜 (YYYY-MM-DD)
 * @example
 * getFirstDayOfMonth("2024-03-14") // "2024-03-01"
 */
export const getFirstDayOfMonth = (date: Date | string) => {
  return formatDate(dayjs(date).startOf("month").toDate());
};

/**
 * 날짜가 유효한지 확인
 * @param {Date | string} date - 확인할 날짜
 * @returns {boolean} 유효한 날짜 여부
 * @example
 * isValidDate("2024-03-14") // true
 * isValidDate("2024-13-14") // false
 * isValidDate("invalid") // false
 */
export const isValidDate = (date: Date | string) => {
  return dayjs(date).isValid();
};

/**
 * 현재 시간을 HH:mm:ss 형식으로 반환
 * @returns {string} 현재 시간
 * @example
 * getCurrentTime() // "14:30:45"
 */
export const getCurrentTime = () => {
  return dayjs().format("HH:mm:ss");
};

/**
 * 날짜와 시간을 YYYY-MM-DD HH:mm:ss 형식으로 반환
 * @param {Date | string} date - 변환할 날짜
 * @returns {string} 날짜와 시간
 * @example
 * formatDateTime(new Date()) // "2024-03-14 14:30:45"
 */
export const formatDateTime = (date: Date | string) => {
  return dayjs(date).format("YYYY-MM-DD HH:mm:ss");
};

/**
 * 상대적 시간 표시 (예: 3일 전, 2시간 전)
 * @param {Date | string} date - 기준 날짜
 * @returns {string} 상대적 시간
 * @example
 * getRelativeTime("2024-03-13") // "1일 전"
 * getRelativeTime("2024-03-14 13:00:00") // "1시간 전"
 */
export const getRelativeTime = (date: Date | string) => {
  return dayjs(date).fromNow();
};

export const formatDateToKorean = (isoString: string): string => {
  try {
    const date = new Date(isoString);

    // 유효한 날짜인지 확인
    if (isNaN(date.getTime())) {
      return isoString; // 변환 실패 시 원본 반환
    }

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");

    return `${year}.${month}.${day} ${hours}:${minutes}`;
  } catch (error) {
    console.error("날짜 변환 오류:", error);
    return isoString; // 오류 발생 시 원본 반환
  }
};

/**
 * 날짜를 yyyy.mm.dd 형태로 포맷팅 (화면 노출용)
 * @param {Date | string | null} date - 포맷팅할 날짜
 * @returns {string} yyyy.mm.dd 형태의 문자열
 * @example
 * formatDateDot(new Date("2024-03-14")) // "2024.03.14"
 * formatDateDot(null) // ""
 */
export const formatDateDot = (date: Date | string | null) => {
  if (!date) return "";
  return dayjs(date).format("YYYY.MM.DD");
};
